import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { employeeApi } from "../../services/employeeApi";
import { formatApiErrorDetail } from "../../lib/api";
import { EMPLOYMENT_TYPES } from "../../lib/constants";
import { useLocalization } from "../../hooks/useLocalization";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { StoreSelect } from "../../components/StoreSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { cn } from "../../lib/utils";

const FIELD = "w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const LABEL = "text-sm font-medium text-gray-700 mb-1 block";

export default function EmployeeCreatePage() {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    legal_name: "", preferred_name: "", japanese_name: "", katakana_name: "",
    personal_email: "", work_email: "", phone: "", address: "",
    employment_type: "FULL_TIME", position: "", department: "", store_location: "",
    proposed_start_date: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.proposed_start_date) delete payload.proposed_start_date;
      const { data } = await employeeApi.create(payload);
      navigate(`/management/employees/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="employee-create-page">
      <PageHelpBanner pageKey="employee-create" title="How to use this page">
        <p>Fill in the fields below to create a new employee master record. Only legal name, personal email, position, and store location are required - everything else can be added later from the employee's Profile page. This creates the record only; onboarding checklist items, wage setup, and account access are handled separately.</p>
      </PageHelpBanner>
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-6">{t("employees.newEmployee")}</h2>
      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={LABEL}>{t("employees.legalName")}</label><input data-testid="input-legal-name" required className={FIELD} value={form.legal_name} onChange={update("legal_name")} /></div>
          <div><label className={LABEL}>{t("employees.preferredName")}</label><input data-testid="input-preferred-name" className={FIELD} value={form.preferred_name} onChange={update("preferred_name")} /></div>
          <div><label className={LABEL}>{t("employees.japaneseName")}</label><input data-testid="input-japanese-name" className={FIELD} value={form.japanese_name} onChange={update("japanese_name")} /></div>
          <div><label className={LABEL}>{t("employees.katakanaName")}</label><input data-testid="input-katakana-name" className={FIELD} value={form.katakana_name} onChange={update("katakana_name")} /></div>
          <div><label className={LABEL}>{t("employees.personalEmail")}</label><input data-testid="input-personal-email" type="email" required className={FIELD} value={form.personal_email} onChange={update("personal_email")} /></div>
          <div><label className={LABEL}>{t("employees.workEmail")}</label><input data-testid="input-work-email" type="email" className={FIELD} value={form.work_email} onChange={update("work_email")} /></div>
          <div><label className={LABEL}>{t("employees.phone")}</label><input data-testid="input-phone" className={FIELD} value={form.phone} onChange={update("phone")} /></div>
          <div><label className={LABEL}>{t("employees.address")}</label><input data-testid="input-address" className={FIELD} value={form.address} onChange={update("address")} /></div>
          <div>
            <label className={LABEL}>{t("employees.employmentType")}</label>
            <Select value={form.employment_type} onValueChange={(v) => setForm((f) => ({ ...f, employment_type: v }))}>
              <SelectTrigger data-testid="input-employment-type" className={FIELD}><SelectValue /></SelectTrigger>
              <SelectContent>
                {EMPLOYMENT_TYPES.map((type) => <SelectItem key={type} value={type} data-testid={`employment-type-option-${type}`}>{type}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><label className={LABEL}>{t("employees.position")}</label><input data-testid="input-position" required className={FIELD} value={form.position} onChange={update("position")} /></div>
          <div><label className={LABEL}>{t("employees.department")}</label><input data-testid="input-department" className={FIELD} value={form.department} onChange={update("department")} /></div>
          <div><label className={LABEL}>{t("employees.storeLocation")}</label><StoreSelect testId="input-store-location" value={form.store_location} onChange={(v) => setForm((f) => ({ ...f, store_location: v || "" }))} /></div>
          <div>
            <label className={LABEL}>{t("employees.proposedStartDate")}</label>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" data-testid="input-start-date" className={cn(FIELD, "flex items-center justify-between text-left", !form.proposed_start_date && "text-gray-500")}>
                  {form.proposed_start_date ? format(new Date(`${form.proposed_start_date}T00:00:00`), "PPP") : "Select a date"}
                  <CalendarIcon size={15} className="text-gray-500" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  selected={form.proposed_start_date ? new Date(`${form.proposed_start_date}T00:00:00`) : undefined}
                  onSelect={(d) => setForm((f) => ({ ...f, proposed_start_date: d ? format(d, "yyyy-MM-dd") : "" }))}
                  data-testid="start-date-calendar"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        {error && <p className="text-sm text-red-500" data-testid="employee-create-error">{error}</p>}
        <button
          data-testid="submit-employee-button"
          type="submit"
          disabled={submitting}
          className="px-6 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
        >
          {submitting ? t("common.loading") : t("common.submit")}
        </button>
      </form>
    </div>
  );
}
